import classNames from 'classnames'
import React, { useMemo } from 'react'

import type { ButtonIconProps } from './types'
import { EMPTY_OBJECT, omit } from '../../utils/object'
import Flex from '../_primitives/flex/flex'
import Icon from '../_primitives/icon/icon'
import svgIcons from '../_primitives/icon/svg-icons'
import ToggleFlipIcon from '../_primitives/icon/toggle-flip-icon'
import type { SvgIcon } from '../_primitives/icon/types'
import { isIconProps } from '../_primitives/icon/types'
import type { SizeProps } from '../_primitives/types'

export function shouldRenderButtonIcon(icon: ButtonIconProps): boolean {
  return (typeof icon === 'string' && icon in svgIcons) || isIconProps(icon)
}

function shouldFlipIcon(icon: ButtonIconProps): boolean {
  return (
    typeof icon === 'object' &&
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (icon as any).hasOwnProperty('shouldFlipIcon') &&
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (icon as any)['shouldFlipIcon']
  )
}

function ButtonIcon(props: {
  buttonLabel?: string
  isLoading?: boolean
  icon: ButtonIconProps
  iconSize?: SizeProps
}): JSX.Element {
  const { buttonLabel, icon, iconSize, isLoading, ...rest } = props
  const iconProps = useMemo(
    () => ({
      icon: isIconProps(icon) ? icon.icon : (icon as SvgIcon),
      size: isIconProps(icon) ? icon.size || iconSize : iconSize,
      sx: {
        '&.is-hidden': {
          opacity: 0
        },
        ...(isIconProps(icon) ? icon.sx : EMPTY_OBJECT)
      },
      ...(isIconProps(icon)
        ? { ...omit(icon, ['icon', 'size', 'sx']), ...rest }
        : rest)
    }),
    [icon, iconSize, rest]
  )

  const Component = shouldFlipIcon(icon) ? ToggleFlipIcon : Icon
  return (
    <Component
      as={Flex}
      className={classNames({
        'is-hidden': isLoading
      })}
      a11yHidden
      a11yLabel={buttonLabel}
      {...iconProps}
    />
  )
}

export default ButtonIcon
